import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pro-only features
export const ProFeatures = {
  FULL_MATCH_ENGINE: 'full_match_engine',
  UNLIMITED_MATCHES: 'unlimited_matches',
  CASE_COMPARISON: 'case_comparison',
  FACE_SEARCH: 'face_search',
  NETWORK_GRAPH: 'network_graph',
  CASE_PACK_PDF: 'case_pack_pdf',
  INTEL_EXTRACTION: 'intel_extraction',
  ADVANCED_MAP: 'advanced_map'
};

// Feature display info
export const FeatureInfo = {
  [ProFeatures.FULL_MATCH_ENGINE]: {
    name: 'Full Match Engine',
    description: 'Access complete match analysis and scoring details',
    icon: 'Scale'
  },
  [ProFeatures.UNLIMITED_MATCHES]: {
    name: 'Unlimited Match Viewing',
    description: 'View all potential matches without limits',
    icon: 'Infinity'
  },
  [ProFeatures.CASE_COMPARISON]: {
    name: 'Case Comparison Tools',
    description: 'Side-by-side comparison of cases with detailed analysis',
    icon: 'Columns'
  },
  [ProFeatures.FACE_SEARCH]: {
    name: 'Face Search',
    description: 'Search cases using facial recognition technology',
    icon: 'ScanFace'
  },
  [ProFeatures.NETWORK_GRAPH]: {
    name: 'Network Graph',
    description: 'Visualize connections between cases and relationships',
    icon: 'Network'
  },
  [ProFeatures.CASE_PACK_PDF]: {
    name: 'Case Pack PDF Export',
    description: 'Export comprehensive case reports as PDF',
    icon: 'FileText'
  },
  [ProFeatures.INTEL_EXTRACTION]: {
    name: 'Intel Extraction (AI)',
    description: 'AI-powered extraction of key information from cases',
    icon: 'Brain'
  },
  [ProFeatures.ADVANCED_MAP]: {
    name: 'Advanced Map Features',
    description: 'Clustering, heat maps, and advanced geographic analysis',
    icon: 'Map'
  }
};

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState(null);

  // Fetch subscription status
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(`${API}/subscription/status`, {
        credentials: 'include',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus, user]);

  // Check if user has Pro access (Pro or Trial)
  const isPro = subscriptionStatus?.is_pro || subscriptionStatus?.is_admin || false;
  
  // Check if trial is available
  const trialAvailable = subscriptionStatus?.trial_available || false;
  
  // Get trial days remaining
  const trialDaysRemaining = subscriptionStatus?.trial_days_remaining;
  
  // Get match limit
  const matchLimit = subscriptionStatus?.match_limit;

  // Check if user can access a feature
  const canAccessFeature = useCallback((feature) => {
    if (!isAuthenticated) return false;
    if (subscriptionStatus?.is_admin) return true;
    if (subscriptionStatus?.is_pro) return true;
    return false;
  }, [isAuthenticated, subscriptionStatus]);

  // Request access to a Pro feature (shows appropriate modal)
  const requestFeatureAccess = useCallback((feature) => {
    if (canAccessFeature(feature)) return true;

    setBlockedFeature(feature);
    
    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return false;
    }

    if (trialAvailable) {
      setShowTrialModal(true);
    } else {
      setShowUpgradeModal(true);
    }
    return false;
  }, [canAccessFeature, isAuthenticated, trialAvailable]);

  // Start trial
  const startTrial = useCallback(async () => {
    try {
      const res = await fetch(`${API}/subscription/start-trial`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscriptionStatus(data.status);
        setShowTrialModal(false);
        return { success: true, message: data.message };
      } else {
        const error = await res.json();
        return { success: false, message: error.detail };
      }
    } catch (error) {
      console.error('Failed to start trial:', error);
      return { success: false, message: 'Failed to start trial' };
    }
  }, []);

  // Close modals
  const closeModals = useCallback(() => {
    setShowUpgradeModal(false);
    setShowTrialModal(false);
    setBlockedFeature(null);
  }, []);

  const value = {
    // Status
    subscriptionStatus,
    loading,
    isPro,
    trialAvailable,
    trialDaysRemaining,
    matchLimit,
    tier: subscriptionStatus?.tier || 'free',
    
    // Feature access
    canAccessFeature,
    requestFeatureAccess,
    
    // Trial
    startTrial,
    
    // Modals
    showUpgradeModal,
    showTrialModal,
    blockedFeature,
    setShowUpgradeModal,
    setShowTrialModal,
    closeModals,
    
    // Refresh
    refreshStatus: fetchSubscriptionStatus
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
